import React, { useLayoutEffect, useState } from "react";
import { usePopper } from "react-popper";
import styled from "styled-components";
import useOnClickOutside from "@/modules/hooks/use-on-click-outside";
import { borderWidth, colors, radius, shadows, spacings } from "../../assets/themes";
import Block from "../block";
import { POPOVER } from "@/components/styles/variants/popover";
import { Body16 } from "../text";
import PortalContent from "./portal-content";

const sameWidthModifier = {
  name: "sameWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
  },
};

const MOUSE_OUTSIDE_HIDE_DELAY = 200;

const setProps = ({ trigger, show: handleShow, hide: handleHide }) => {
  const p = {};
  if (trigger === POPOVER.TRIGGER_TYPE.CLICK) {
    p.onClick = handleShow;
  }
  if (trigger === POPOVER.TRIGGER_TYPE.FOCUS) {
    p.onFocus = handleShow;
    p.onBlur = handleHide;
  }
  if (trigger === POPOVER.TRIGGER_TYPE.HOVER) {
    // onMouseEnter -> on parent event
    p.onMouseEnter = handleShow;
    p.onFocus = handleShow;
    p.onBlur = handleHide;
  }
  return p;
};

const PopoverContainer = ({ children, trigger, show, hide }) =>
  React.Children.map(children, (child) =>
    React.cloneElement(child, {
      ...setProps({ trigger, show, hide }),
    })
  );

const Enhancer = ({
  position = POPOVER.POSITIONS.BOTTOM,
  trigger = POPOVER.TRIGGER_TYPE.HOVER,
  content,
  children,
  mountNode,
  sameWidth,
  onClose,
  onOpen,
  isOpen,
  strategy = POPOVER.STRATEGY.ABSOLUTE,
  onMouseLeaveDelay = MOUSE_OUTSIDE_HIDE_DELAY,
  ...rest
}) => {
  let to;
  const [referenceElement, setReferenceElement] = useState(null);
  const [preventShowAfterOutside, setPreventShowAfterOutside] = useState(false);
  const [popperElement, setPopperElement] = useState(null);
  const [isVisible, setIsVisible] = useState(true); // True by default for popper get default rect, set to False in the layout effect
  const { styles, state } = usePopper(referenceElement, popperElement, {
    placement: position,
    strategy,
    modifiers: [
      ...(sameWidth ? [sameWidthModifier] : []),
      {
        name: "flip",
        options: {
          padding: 16,
          flipVariations: true,
        },
      },
      {
        name: "preventOverflow",
        options: {
          altBoundary: false,
          padding: 16,
        },
      },
      {
        name: "offset",
        options: {
          offset: [sameWidth ? 0 : 8, 8],
        },
      },
    ],
  });

  const handleShow = (e) => {
    e.stopPropagation();
    clearTimeout(to);
    if (!preventShowAfterOutside) {
      if (onOpen) onOpen(e);
      setIsVisible(typeof isOpen === "boolean" ? isOpen : true);
    }
  };

  const handleHide = (opts = {}) => {
    const { noDelay = false } = opts;
    if (noDelay) {
      setIsVisible(typeof isOpen === "boolean" ? isOpen : false);
      if (onClose) onClose();
    } else {
      to = setTimeout(() => {
        setIsVisible(typeof isOpen === "boolean" ? isOpen : false);
        if (onClose) onClose();
      }, onMouseLeaveDelay);
    }
  };

  const handleMouseLeave = () => {
    if (trigger === POPOVER.TRIGGER_TYPE.HOVER) {
      handleHide();
    }
  };

  const handleMouseEnter = () => {
    clearTimeout(to);
  };

  const handleClickOutside = (e) => {
    if (trigger === POPOVER.TRIGGER_TYPE.CLICK && isVisible) {
      setPreventShowAfterOutside(true);
      handleHide(e);
    }
  };

  useLayoutEffect(() => {
    setIsVisible(typeof isOpen === "boolean" ? isOpen : false);
  }, [isOpen]);

  useOnClickOutside({ current: popperElement }, handleClickOutside);

  return (
    <Block {...rest} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div ref={setReferenceElement}>
        <PopoverContainer hide={handleHide} show={handleShow} trigger={trigger}>
          {children}
        </PopoverContainer>
      </div>

      <PortalContent
        isVisible={isVisible}
        mountNode={mountNode}
        onAnimationComplete={() => {
          setPreventShowAfterOutside(false);
        }}
        position={state?.placement || position}
        ref={setPopperElement}
        styles={styles}
      >
        {content && content({ close: () => handleHide({ noDelay: true }) })}
      </PortalContent>
    </Block>
  );
};

const StyledTooltip = styled.div`
  border-radius: ${radius.l};
  padding: ${spacings.sm} ${spacings.m};
  background-color: ${colors.backgroundDark};
  max-width: ${POPOVER.TOOLTIP_MAX_WIDTH}px;
  z-index: 999;
`;

const Tooltip = ({ children }) => (
  <StyledTooltip>
    <Body16 color={colors.onColor}>{children}</Body16>
  </StyledTooltip>
);

const Menu = styled.div`
  border-radius: ${radius.l};
  border: solid ${borderWidth.s} ${colors.border};
  background-color: ${colors.background};
  box-shadow: ${shadows.m};
  padding-top: ${spacings.xs};
  padding-bottom: ${spacings.xs};
  overflow: hidden;
`;

export default { Enhancer, Elem: { Tooltip, Menu } };
